import React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import { useCatalogEntity, useTranslation } from 'Common/hooks';
import { Modal } from 'Common/components/ui';

function serialize(params) {
    return Object.keys(params)
        .filter((key) => params[key] != null && params[key] !== '')
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
}

// Builds the locally.com embedded store-locator URL shown inside the modal iframe.
// This mirrors the iframe URL locally's own map.js loader builds for the UPC
// ("Find it Locally") flow — `{origin}/stores/map/embedded?action=convert&{query}`
// — but rendered in our own modal so it isn't limited to a single widget per page
// (locally's loader is hardcoded to a single `lcly-button-0`).
function buildEmbeddedUrl(endpointUrl, params) {
    let origin;

    try {
        origin = new URL(endpointUrl).origin;
    } catch {
        return null;
    }

    const query = `${serialize(params)}&host_domain=${encodeURIComponent(window.location.hostname)}`;

    return `${origin}/stores/map/embedded?action=convert&${query}#/?${query}`;
}

/**
 * "Find it Locally" button + modal for a single product, matching the PDP
 * (see CommonProduct.Locally.cshtml). The product's variant UPCs are fetched
 * client-side so the locator shows dealers carrying that specific product.
 */
export default function LocallyWidget({ productCode, productUrl, companyName, companyId, endpointUrl, className }) {
    const [productInfo] = useCatalogEntity(productCode);
    const [isOpen, setIsOpen] = React.useState(false);

    const buttonText = useTranslation('Commerce.Product.Compare.FindItLocally', null, {
        notFoundValue: 'Find it Locally',
    });

    const upcs = React.useMemo(
        () =>
            (productInfo?.Children || [])
                .map((child) => child.UPC)
                .filter(Boolean)
                .join(','),
        [productInfo]
    );

    const iframeUrl = React.useMemo(() => {
        if (!upcs || !companyId || !endpointUrl) {
            return null;
        }

        return buildEmbeddedUrl(endpointUrl, {
            company_id: companyId,
            dealers_company_id: companyId,
            company_name: companyName,
            upc: upcs,
            show_dealers: '1',
            n_dealers: '3',
            show_address: '1',
            show_phone: '1',
            show_location_prompt: '1',
            radius_in_miles: '100',
            lang: 'en-us',
        });
    }, [upcs, companyId, companyName, endpointUrl]);

    const onOpen = React.useCallback((event) => {
        event.preventDefault();
        setIsOpen(true);
    }, []);

    const onClose = React.useCallback(() => setIsOpen(false), []);

    if (!companyId || !endpointUrl) {
        return null;
    }

    // Product loaded but no UPCs (or a malformed endpoint): Locally can't show
    // product-specific dealers, so fall back to the PDP where the page-level
    // locator still applies.
    if (productInfo && !iframeUrl) {
        return (
            <a className={cx('btn btn-secondary', className)} href={productUrl}>
                <i className="fa fa-map-marker-alt me-2" aria-hidden="true"></i>
                {buttonText}
            </a>
        );
    }

    return (
        <>
            <button type="button" className={cx('btn btn-secondary', className)} onClick={onOpen}>
                <i className="fa fa-map-marker-alt me-2" aria-hidden="true"></i>
                {buttonText}
            </button>
            <Modal
                className="LocallyModal"
                modalSize="lg"
                showModal={isOpen}
                onClose={onClose}
                header={<h5 className="modal-title">{buttonText}</h5>}
                body={
                    isOpen && iframeUrl ? (
                        <iframe className="LocallyModal__iframe" title={buttonText} src={iframeUrl} />
                    ) : null
                }
            />
        </>
    );
}

LocallyWidget.propTypes = {
    productCode: px.string,
    productUrl: px.string,
    companyName: px.string,
    companyId: px.string,
    endpointUrl: px.string,
    className: px.string,
};
